import { useEffect, useState, useRef, useMemo } from "react";
import { fetchStudentList } from "../../services/studentService";
import { generateInvoice } from "../../services/invoiceService";
import { X, Loader2, AlertCircle } from "lucide-react";
import "./GenerateInvoiceModal.css";

const GenerateInvoiceModal = ({ onClose, onSuccess }) => {
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(true);
  
  const [courseDetailsId, setCourseDetailsId] = useState("");
  const [billingType, setBillingType] = useState("MONTHLY");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");

  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [successData, setSuccessData] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredStudents = useMemo(() => {
    if (!searchQuery) return students;
    const query = searchQuery.toLowerCase();
    return students.filter(s => {
      const name = (s.StudentName || s.Name || "").toLowerCase();
      const phone = (s.PhoneNumber || s.ParentPhoneNumber || s.ContactPhone || "").toLowerCase();
      return name.includes(query) || phone.includes(query);
    });
  }, [students, searchQuery]);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const res = await fetchStudentList();
        setStudents(res?.data || []);
      } catch (err) {
        console.error("Failed to fetch students", err);
        setError("Failed to load students.");
      } finally {
        setLoadingStudents(false);
      }
    };
    fetchStudents();
  }, []);

  const handleGenerate = async () => {
    if (!courseDetailsId || Number(courseDetailsId) <= 0) {
      setError("Please select a student/course before generating the invoice.");
      return;
    }

    if (!billingType || !periodStart || !periodEnd) {
      setError("Please fill all required fields.");
      return;
    }
    
    if (periodStart > periodEnd) {
      setError("Period Start cannot be after Period End.");
      return;
    }

    try {
      setGenerating(true);
      setError("");
      
      const payload = {
        courseDetailsId: Number(courseDetailsId),
        billingType,
        periodStart,
        periodEnd
      };

      console.log("Generate Invoice payload:", payload);

      const res = await generateInvoice(payload);
      
      if (res && res.length > 0) {
        setSuccessData({
          invoices: res,
          billingType,
          periodStart,
          periodEnd
        });
      } else {
        setError("No invoices generated. They may already exist for this period.");
      }
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.Message || "Failed to generate invoices.");
    } finally {
      setGenerating(false);
    }
  };



  const isFormValid =
    Number(courseDetailsId) > 0 &&
    !!billingType &&
    !!periodStart &&
    !!periodEnd &&
    periodStart <= periodEnd;

  if (successData) {
    return (
      <div className="modal-backdrop">
        <div className="modal-card generate-invoice-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Invoice Generated Successfully</h3>
          </div>
          <div className="modal-body">
            <p><strong>Billing Type:</strong> {successData.billingType}</p>
            <p><strong>Billing Period:</strong> {successData.periodStart} to {successData.periodEnd}</p>
            <div className="preview-list">
              {successData.invoices.map((inv, idx) => (
                <div key={idx} className="preview-item">
                  {inv.InvoiceNumber && <p><strong>Invoice Number:</strong> {inv.InvoiceNumber}</p>}
                  {inv.InvoiceAmount !== undefined && <p><strong>Amount:</strong> ₹{inv.InvoiceAmount}</p>}
                  {inv.StudentName && <p><strong>Student:</strong> {inv.StudentName}</p>}
                </div>
              ))}
            </div>
          </div>
          <div className="modal-footer">
            <button 
              className="invoice-secondary-btn" 
              onClick={() => {
                setSuccessData(null);
                setCourseDetailsId("");
                setBillingType("MONTHLY");
                setPeriodStart("");
                setPeriodEnd("");
                setError("");
              }}
            >
              Generate Another
            </button>
            <button 
              className="invoice-primary-btn" 
              onClick={() => {
                setSuccessData(null);
                onSuccess();
                onClose();
              }}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card generate-invoice-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Generate Invoice</h3>
          <button className="close-btn" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="invoice-alert">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          <div className="form-grid">
            <div className="form-group">
              <label>Student</label>
              <div className="custom-dropdown-container" ref={dropdownRef}>
                <div 
                  className={`custom-dropdown-header ${loadingStudents ? 'disabled' : ''}`}
                  onClick={() => !loadingStudents && setDropdownOpen(!dropdownOpen)}
                >
                  {courseDetailsId 
                    ? (() => {
                        const sel = students.find(s => String(s.CourseDetailsId) === String(courseDetailsId));
                        return sel ? `${sel.StudentName || sel.Name} (${sel.CourseName})` : "All Students (Entire Group)";
                      })()
                    : "All Students (Entire Group)"}
                </div>
                
                {dropdownOpen && (
                  <div className="custom-dropdown-list">
                    <div className="custom-dropdown-search">
                      <input 
                        type="text" 
                        placeholder="Search student name or mobile number..." 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    </div>
                    
                    <div 
                      className={`custom-dropdown-item ${!courseDetailsId ? 'selected' : ''}`}
                      onClick={() => {
                        setCourseDetailsId("");
                        setDropdownOpen(false);
                        setSearchQuery("");
                      }}
                    >
                      <div className="custom-dropdown-item-title">All Students (Entire Group)</div>
                    </div>
                    
                    {filteredStudents.length > 0 ? (
                      filteredStudents.map((s) => {
                        const mobile = s.PhoneNumber || s.ParentPhoneNumber || s.ContactPhone;
                        return (
                          <div 
                            key={s.CourseDetailsId}
                            className={`custom-dropdown-item ${String(courseDetailsId) === String(s.CourseDetailsId) ? 'selected' : ''}`}
                            onClick={() => {
                              setCourseDetailsId(String(s.CourseDetailsId));
                              setDropdownOpen(false);
                              setSearchQuery("");
                            }}
                          >
                            <div className="custom-dropdown-item-title">{s.StudentName || s.Name}</div>
                            <div className="custom-dropdown-item-subtitle">
                              Course: {s.CourseName || "-"}
                              {s.BatchName && ` | Batch: ${s.BatchName}`}
                              {mobile && ` | Mobile: ${mobile}`}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="custom-dropdown-empty">
                        No students found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Billing Type</label>
              <select value={billingType} onChange={(e) => setBillingType(e.target.value)}>
                <option value="MONTHLY">Monthly</option>
                <option value="TERM">Term</option>
                <option value="ANNUAL">Annual</option>
              </select>
            </div>

            <div className="form-group">
              <label>Period Start</label>
              <input 
                type="date" 
                value={periodStart} 
                onChange={(e) => setPeriodStart(e.target.value)} 
              />
            </div>

            <div className="form-group">
              <label>Period End</label>
              <input 
                type="date" 
                value={periodEnd} 
                onChange={(e) => setPeriodEnd(e.target.value)} 
              />
            </div>
          </div>

        </div>

        <div className="modal-footer">
          <button className="invoice-secondary-btn" onClick={onClose} disabled={generating}>Cancel</button>
          <button 
            className="invoice-primary-btn" 
            onClick={handleGenerate} 
            disabled={!isFormValid || generating}
          >
            {generating ? <Loader2 size={16} className="spin" /> : "Generate Invoice"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenerateInvoiceModal;
